import { Box, CircularProgress, Typography, Stack, Skeleton, useTheme } from '@mui/material';
import { useState, useEffect, memo, useCallback } from 'react';
import { keyframes } from '@mui/system';

// Animation pour le spinner principal (simplifiée)
const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
`;

// Animation shimmer supprimée pour optimiser les performances

// Animation pour les barres de progression
const progressAnimation = keyframes`
  0% {
    width: 0%;
  }
  100% {
    width: var(--target-width);
  }
`;

// Animation pour l'apparition des skeletons
const skeletonAppear = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animation pour le défilement horizontal (push effect) - améliorée
const horizontalPush = keyframes`
  0% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(-110px);
  }
  100% {
    transform: translateX(-220px);
  }
`;

interface AdsLoadingScreenProps {
    sx?: any;
    externalLoadingState?: boolean; // Contrôle externe du chargement
}

// Hook personnalisé pour générer un flux continu de blocs (optimisé)
function useInfiniteBlocks(initialCount: number, maxCount: number = 8) {
    const [blocks, setBlocks] = useState<number[]>(() => 
        Array.from({ length: initialCount }, (_, i) => i)
    );
    const [isComplete, setIsComplete] = useState(false);
    
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        let intervalId: NodeJS.Timeout;
        
        intervalId = setInterval(() => {
            setBlocks((prev: number[]) => {
                if (prev.length >= maxCount) {
                    // Délai de 1s avant de marquer comme terminé
                    timeoutId = setTimeout(() => {
                        setIsComplete(true);
                    }, 1000);
                    return prev; // Arrête d'ajouter des blocs après maxCount
                }
                return [...prev, prev.length];
            });
        }, 800); // Ajoute un nouveau bloc toutes les 800ms (plus lent)
        
        return () => {
            clearInterval(intervalId);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [maxCount]);

    return { blocks, isComplete };
}

// Composant mémorisé pour éviter les re-renders inutiles
const PlaBlock = memo(({ i, index, targetWidth, delay, duration, skeletonDelay }: {
    i: number;
    index: number;
    targetWidth: number;
    delay: number;
    duration: number;
    skeletonDelay: number;
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    
    return (
    <Box sx={{ minWidth: 200, maxWidth: 200, position: 'relative' }}>
        {/* Barre de progression pour chaque carte */}
        <Box sx={{ position: 'absolute', top: -8, left: 0, right: 0, zIndex: 1 }}>
            <Box sx={{ width: '100%', height: 2, backgroundColor: isDark ? '#2D3239' : '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                <Box
                    sx={{
                        '--target-width': `${targetWidth}%`,
                        width: 0,
                        height: '100%',
                        background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                        borderRadius: 1,
                        animation: `${progressAnimation} ${duration}s ease-out forwards`,
                        animationDelay: `${delay}s`,
                    }}
                />
            </Box>
        </Box>
        
        <Box
            sx={{
                opacity: 0,
                animation: `${skeletonAppear} 0.3s ease-out forwards`,
                animationDelay: `${skeletonDelay}s`,
            }}
        >
            <Skeleton
                variant="rectangular"
                width={200}
                height={80}
                sx={{
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: isDark ? '#2D3239' : '#e8e8e8',
                }}
            />
            <Skeleton width="80%" height={16} sx={{ mb: 0.5, backgroundColor: isDark ? '#2D3239' : '#e8e8e8' }} />
            <Skeleton width="60%" height={14} sx={{ backgroundColor: isDark ? '#2D3239' : '#e8e8e8' }} />
        </Box>
    </Box>
    );
});

const TextBlock = memo(({ i, index, targetWidth, delay, duration, skeletonDelay }: {
    i: number;
    index: number;
    targetWidth: number;
    delay: number;
    duration: number;
    skeletonDelay: number;
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    
    return (
    <Box sx={{ position: 'relative' }}>
        {/* Barre de progression pour chaque text ad */}
        <Box sx={{ position: 'absolute', top: -6, left: 0, right: 0, zIndex: 1 }}>
            <Box sx={{ width: '100%', height: 2, backgroundColor: isDark ? '#2D3239' : '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                <Box
                    sx={{
                        '--target-width': `${targetWidth}%`,
                        width: 0,
                        height: '100%',
                        background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                        borderRadius: 1,
                        animation: `${progressAnimation} ${duration}s ease-out forwards`,
                        animationDelay: `${delay}s`,
                    }}
                />
            </Box>
        </Box>
        
        <Box
            sx={{
                opacity: 0,
                animation: `${skeletonAppear} 0.3s ease-out forwards`,
                animationDelay: `${skeletonDelay}s`,
            }}
        >
            <Skeleton
                variant="rectangular"
                height={60}
                sx={{
                    borderRadius: 1,
                    backgroundColor: isDark ? '#2D3239' : '#e8e8e8',
                }}
            />
        </Box>
    </Box>
    );
});

export default function AdsLoadingScreen({ sx, externalLoadingState = true, ...other }: AdsLoadingScreenProps ) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { blocks: plaBlocks, isComplete: plaComplete } = useInfiniteBlocks(3, 6); // Commence avec 3 blocs, max 6
    const { blocks: textBlocks, isComplete: textComplete } = useInfiniteBlocks(2, 5); // Commence avec 2 blocs, max 5
    
    // Le loader ne se termine que si l'état externe indique que le chargement est fini
    // Les animations internes continuent mais n'affectent pas l'affichage du loader
    const shouldShowLoader = externalLoadingState;

  // Apparition séquentielle (gauche -> droite) des mots-clés en vue compacte
  const MAX_COMPACT = 5;
  const [compactCount, setCompactCount] = useState<number>(0);
  const [compactExpanded, setCompactExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (!shouldShowLoader) return;
    setCompactCount(0);
    setCompactExpanded(false);
    let current = 0;
    const intervalId = setInterval(() => {
      current += 1;
      setCompactCount((prev) => Math.min(prev + 1, MAX_COMPACT));
      if (current >= MAX_COMPACT) {
        clearInterval(intervalId);
      }
    }, 180);
    const expandTimeout = setTimeout(() => setCompactExpanded(true), 1500);
    return () => {
      clearInterval(intervalId);
      clearTimeout(expandTimeout);
    };
  }, [shouldShowLoader]);
    
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 400,
                p: 3,
                ...sx,
            }}
            {...other}
        >

      {/* Top attacked search terms skeleton (compact then expands) */}
      <Box sx={{ width: '100%', maxWidth: 800, mb: 2 }}>
        {/* Header skeleton (title + icon button) */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Skeleton width={200} height={16} sx={{ backgroundColor: isDark ? '#2D3239' : '#e8e8e8' }} />
          <Skeleton variant="circular" width={20} height={20} sx={{ backgroundColor: isDark ? '#2D3239' : '#e8e8e8' }} />
        </Box>

        {!compactExpanded ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${MAX_COMPACT}, 1fr)`, columnGap: 2 }}>
            {Array.from({ length: MAX_COMPACT }).map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  ...(idx > 0 && { borderLeft: `1px solid ${isDark ? '#2D3239' : '#e0e0e0'}` }),
                }}
              >
                <Skeleton
                  width="60%"
                  height={12}
                  sx={{ flex: '0 1 auto', backgroundColor: isDark ? '#2D3239' : '#e8e8e8' }}
                />
                <Skeleton
                  width={28}
                  height={12}
                  sx={{ flex: '0 0 auto', backgroundColor: isDark ? '#2D3239' : '#e8e8e8' }}
                />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ width: '100%', height: 6, backgroundColor: isDark ? '#2D3239' : '#e0e0e0', borderRadius: 999, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        '--target-width': `${60 + idx * 8}%`,
                        width: 0,
                        height: '100%',
                        background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                        borderRadius: 999,
                        animation: idx < compactCount ? `${progressAnimation} 0.8s ease-out forwards` : 'none',
                        animationDelay: idx < compactCount ? `${idx * 0.1}s` : '0s',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box>
            <Stack spacing={1.25}>
              {Array.from({ length: 5 }).map((_, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Skeleton width={160} height={14} sx={{ flexShrink: 0, backgroundColor: isDark ? '#2D3239' : '#e8e8e8' }} />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ width: '100%', height: 8, backgroundColor: isDark ? '#2D3239' : '#e0e0e0', borderRadius: 999, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          '--target-width': `${50 + idx * 10}%`,
                          width: 0,
                          height: '100%',
                          background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                          borderRadius: 999,
                          animation: `${progressAnimation} 0.8s ease-out forwards`,
                          animationDelay: `${idx * 0.1}s`,
                        }}
                      />
                    </Box>
                  </Box>
                  <Skeleton width={32} height={14} sx={{ flexShrink: 0, backgroundColor: isDark ? '#2D3239' : '#e8e8e8' }} />
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Box>

            {/* Skeletons avec barres de progression individuelles */}
            <Stack spacing={2} sx={{ width: '100%', maxWidth: 800 }}>
        {/* Cartes PLA optimisées */}
        <Box sx={{ display: 'flex', gap: 1.5, overflow: 'hidden', pb: 1, position: 'relative' }}>
          {plaBlocks.map((i, index) => {
            const targetWidth = 100; // Toutes les barres vont à 100%
            const delay = index * 0.2; // Délai plus espacé
            const duration = 0.5; // Durée fixe pour éviter les calculs aléatoires
            const skeletonDelay = delay + duration * 0.3; // Skeleton apparaît à 30% de la progression
            
            return (
              <PlaBlock
                key={i}
                i={i}
                index={index}
                targetWidth={targetWidth}
                delay={delay}
                duration={duration}
                skeletonDelay={skeletonDelay}
              />
            );
          })}
        </Box>

        {/* Text ads optimisés */}
        <Stack spacing={1}>
          {textBlocks.map((i, index) => {
            const targetWidth = 100; // Toutes les barres vont à 100%
            const delay = (index + 3) * 0.25; // Délai plus espacé
            const duration = 0.4; // Durée fixe
            const skeletonDelay = delay + duration * 0.3; // Skeleton apparaît à 30% de la progression
            return (
              <TextBlock
                key={i}
                i={i}
                index={index}
                targetWidth={targetWidth}
                delay={delay}
                duration={duration}
                skeletonDelay={skeletonDelay}
              />
            );
          })}
        </Stack>
      </Stack>
        </Box>
    );
}
